<script lang="ts">
  import type { EstimatedTimeName } from '@gat-ship/types'

  export interface ShowEditEstimatedTimeNameParams {
    entry: EstimatedTimeName
  }
</script>

<script setup lang="ts">
  import { ref } from 'vue'
  import { IconEstimatedTime, IconTrash } from '@gat-ship/icons'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { useEstimatedTimeNames } from '../../../../composables/useEstimatedTimeNames'

  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useEstimatedTimeNames()

  const editingId = ref<string | undefined>(undefined)

  const referenceTimePattern = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

  const schema = z.object({
    code: z.string().min(1, 'Required').max(10, 'Max 10 characters'),
    name: z.string().min(1, 'Required').max(40, 'Max 40 characters'),
    referenceTime: z
      .string()
      .regex(referenceTimePattern, 'Use HH:mm or HH:mm:ss'),
    isInactive: z.boolean()
  })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      code: '',
      name: '',
      referenceTime: '',
      isInactive: false
    } as FormValues,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      if (!editingId.value) return
      const referenceTime =
        value.referenceTime.length === 5
          ? `${value.referenceTime}:00`
          : value.referenceTime
      await service.update(editingId.value, {
        code: value.code,
        name: value.name,
        referenceTime,
        isInactive: value.isInactive
      })
      isOpen.value = false
    }
  })

  async function handleDelete() {
    if (!editingId.value) return
    await service.remove(editingId.value)
    isOpen.value = false
  }

  function showModal({ entry }: ShowEditEstimatedTimeNameParams) {
    editingId.value = entry.id
    form.reset({
      code: entry.code,
      name: entry.name,
      referenceTime: entry.referenceTime,
      isInactive: entry.isInactive
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEstimatedTime class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Edit estimated time name
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        Update the code, label, or anchor time. Inactive entries stay in the
        registry but are hidden from selectors.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="code" label="Code" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="referenceTime"
            label="Reference time"
            required
            hint="24-hour clock; drives sort order and ETA lookup."
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                placeholder="HH:mm:ss"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button variant="ghost" @click="handleDelete">
        <IconTrash class="size-3.5" />
        Delete
      </Button>
      <Button class="ml-auto" variant="ghost" @click="isOpen = false">
        Cancel
      </Button>
      <FormSubmit :form="form" label="Save" :require-dirty="false" />
    </ModalFooter>
  </modal>
</template>
